import React, { useEffect, useState } from 'react';
import { Card, CardBody, Button, FormGroup } from 'reactstrap';
import FacilitySelect from '../../shared/components/inputs/FacilitySelect/FacilitySelect';
import config, { USER_TYPES } from '../../config/app.config';
import validateStaffForm from './staffValidate';
import generatePassword from '../../helpers/generatePassword';
import { isPrimaryAdmin, isSuperAdmin } from '../../utils/Utils';
import { InputLabel } from '../../shared/components/InputLabel';
import DesignationSelect from '../../shared/components/inputs/DesignationSelect/DesignationSelect';
import localStorage from '../../libs/storageHelper';
import { FormInput } from './components/FormInput';
import { PermissionsSelect } from './components/PermissionsSelect';
import Error from '../../shared/components/form/Error';
import { ResponseMessage } from '../../shared/components/ResponseMessage';

const privateCredExpl =
  'The staff member receives an email invitation, signs in with their email address and temporary password, and then creates their own password.';
const setCredExpl =
  'The facility creates and manages the username and password. Use this for shared accounts or when the facility does not want individual staff invitations.';

const defaultForm = {
  firstName: '',
  lastName: '',
  email: '',
  facility: null,
  designation: '',
  type: USER_TYPES.STAFF,
  username: '',
  phone: '',
  extension: '',
  password: '',
  internalNotes: '',
  privateCredentials: true,
};

const defaultErrors = {
  firstName: null,
  lastName: null,
  email: null,
  facility: null,
  designation: null,
  username: null,
  phone: null,
  extension: null,
  type: null,
  password: null,
  privateCredentials: null,
  internalNotes: null,
};

export default function StaffForm({
  handleSubmit,
  closeModal = () => {},
  onSubmit = () => {},
  onPrivateCredentialsChange = () => {},
  successResponse = '',
  errorResponse = '',
}) {
  const [loggedUser] = useState(localStorage.getFromStorage('loggedUser').user);
  const [errors, setErrors] = useState({ ...defaultErrors });
  const [inputs, setForm] = useState({
    ...defaultForm,
    facility:
      loggedUser?.type === config.USER_TYPES.SUPER_ADMIN
        ? ''
        : loggedUser?.facility,
  });

  const showInternalNotes =
    isPrimaryAdmin(loggedUser) || isSuperAdmin(loggedUser);

  const onChangeFields = (e) => {
    const { name, type, checked, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((err) => ({
      ...err,
      [name]: null,
    }));
  };

  const setCredentialMode = (isPrivate) => {
  onPrivateCredentialsChange(isPrivate);
  setForm((f) => ({
    ...f,
    privateCredentials: isPrivate,
    type: isPrivate ? USER_TYPES.STAFF : USER_TYPES.IPAD_ONLY,
    username: isPrivate ? '' : f.username || f.email || '',
    password: '',
  }));
    setErrors((err) => ({
      ...err,
      email: null,
      username: null,
      password: null,
    }));
  };

  const validateAndSubmit = () => {
    const isPrivate = inputs.privateCredentials;

    const staff = {
      ...inputs,
      firstName: (inputs.firstName || '').trim(),
      lastName: (inputs.lastName || '').trim(),
      email: isPrivate ? (inputs.email || '').trim() : '',
      username: isPrivate
        ? (inputs.email || '').trim()
        : (inputs.username || '').trim(),
      password: isPrivate ? generatePassword() : inputs.password,
      privateCredentials: isPrivate,
      internalNotes: showInternalNotes
        ? String(inputs.internalNotes || '').trim()
        : undefined,
    };

    const { errors: nextErrors, error } = validateStaffForm(staff);

    if (error) {
      return setErrors(nextErrors);
    }

    staff.phone = staff.phone
      ? staff.phone.replace(/[^A-Z0-9]+/gi, '')
      : staff.phone;

    onSubmit(staff);
  };

  const handleSelectFacility = (facility) => {
    setForm((f) => ({
      ...f,
      facility,
    }));
  };

  useEffect(() => {
    setForm((f) => ({
      ...f,
      facility: isSuperAdmin(loggedUser) ? '' : loggedUser?.facility,
    }));
  }, [loggedUser]);

  const isPrivate = inputs.privateCredentials;

  return (
    <Card>
      <CardBody>
        <form
          className="form staff-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (handleSubmit) handleSubmit(e);
          }}
        >
          <div className="staff-modal-sticky-top">
            <ResponseMessage
              type={successResponse ? 'success' : 'danger'}
              message={successResponse || errorResponse}
            />
            <div className="staff-credential-picker">
              <div className="staff-credential-option">
                <div className="checkbox-wrapper">
                  <label className="checkbox-container terms-and-conditions mb-0">
                    <input
                      id="privateCredentials"
                      className="custom-checkbox"
                      type="checkbox"
                      checked={isPrivate}
                      onChange={() => setCredentialMode(true)}
                    />
                    <span className="checkmark" />
                    <label
                      className="form-check-label"
                      htmlFor="privateCredentials"
                    >
                      Private Credentials
                    </label>
                  </label>
                </div>
                <p className="staff-credential-help">{privateCredExpl}</p>
              </div>

              <div className="staff-credential-option">
                <div className="checkbox-wrapper">
                  <label className="checkbox-container terms-and-conditions mb-0">
                    <input
                      id="assignedCredentials"
                      className="custom-checkbox"
                      type="checkbox"
                      checked={!isPrivate}
                      onChange={() => setCredentialMode(false)}
                    />
                    <span className="checkmark" />
                    <label
                      className="form-check-label"
                      htmlFor="assignedCredentials"
                    >
                      Assigned Credentials
                    </label>
                  </label>
                </div>
                <p className="staff-credential-help">{setCredExpl}</p>
              </div>
            </div>
          </div>

          <div className="staff-modal-scroll container-fluid">
            {!isPrivate && (
              <p className="staff-name-helper">
                If entering a name, both First and Last Name are required.
              </p>
            )}
            <div className="row">
              <FormInput
                wrapperClassName="col-md-6"
                label="First Name"
                name="firstName"
                value={inputs.firstName}
                onChange={onChangeFields}
                error={errors.firstName}
                required={isPrivate}
                optional={!isPrivate}
              />
              <FormInput
                wrapperClassName="col-md-6"
                label="Last Name"
                name="lastName"
                value={inputs.lastName}
                onChange={onChangeFields}
                error={errors.lastName}
                required={isPrivate}
                optional={!isPrivate}
              />
            </div>

            <div className="row">
              {isSuperAdmin(loggedUser) && (
                <div className="col-md-6">
                  <div className="form__form-group">
                    <InputLabel required>Facility</InputLabel>
                    <div className="form__form-group-field">
                      <FacilitySelect
                        onChange={handleSelectFacility}
                        value={inputs.facility}
                        error={errors.facility}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="col-md-6">
                <DesignationSelect
                  onChange={onChangeFields}
                  value={inputs.designation}
                  error={errors.designation}
                  required={isPrivate}
                  optional={!isPrivate}
                />
              </div>
              <div className="col-md-6">
                <PermissionsSelect
                  onChange={onChangeFields}
                  value={inputs.type}
                  isPrivate={isPrivate}
                />
              </div>
            </div>

            <div className="row">
              <FormInput
                wrapperClassName="col-md-6"
                label="Email"
                name="email"
                value={inputs.email}
                onChange={onChangeFields}
                error={errors.email}
                required={isPrivate}
                disabled={!isPrivate}
                autoComplete="off"
                labelNote={
                  isPrivate ? '' : 'not used for Assigned Credentials'
                }
              />
              <FormInput
                wrapperClassName="col-md-6"
                label="Username"
                name="username"
                value={isPrivate ? inputs.email : inputs.username}
                onChange={onChangeFields}
                error={errors.username}
                required={!isPrivate}
                readOnly={isPrivate}
                autoComplete="off"
                labelNote={isPrivate ? 'same as Email' : ''}
              />
            </div>

            <div className="row">
              <FormInput
                wrapperClassName="col-md-6"
                label="Password"
                name="password"
                type={isPrivate ? 'text' : 'password'}
                value={
                  isPrivate
                    ? 'Set by invited Staff Member after accepting their invitation.'
                    : inputs.password
                }
                onChange={onChangeFields}
                error={errors.password}
                required={!isPrivate}
                disabled={isPrivate}
                autoComplete="new-password"
                inputClassName={isPrivate ? 'staff-password-disabled' : ''}
              />
              {showInternalNotes && (
                <div className="col-md-6">
                  <div className="form__form-group">
                    <InputLabel>
                      Internal Notes
                      <span className="staff-field-note">(optional)</span>
                    </InputLabel>
                    <div className="form__form-group-field">
                      <FormGroup>
                        <textarea
                          className="form-control"
                          name="internalNotes"
                          value={inputs.internalNotes}
                          onChange={onChangeFields}
                          rows={3}
                          maxLength={500}
                        />
                        {errors.internalNotes && (
                          <Error text={errors.internalNotes} />
                        )}
                      </FormGroup>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="row staff-form-footer">
            <div className="col-md-6" />
            <div className="col-md-6 text-right">
              <Button
                className="clear"
                color="secondary"
                onClick={closeModal}
              >
                Cancel
              </Button>
              <Button
                className="save-update"
                color="primary"
                onClick={validateAndSubmit}
              >
                {isPrivate ? 'Save and Invite' : 'Save'}
              </Button>
            </div>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
